import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const benefits = [
  {
    icon: 'ri-user-heart-line',
    title: 'We Work For YOU, Not the Insurance Company',
    description:
      "Insurance adjusters work for the insurance company — their job is to minimize your payout. We are public adjusters hired by you to maximize your settlement. Our interests are 100% aligned with yours.",
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'We Maximize Your Payout',
    description:
      'Our clients receive on average 3-4x more than their initial insurance offer. We know the policy language, the negotiation tactics, and exactly how to document damage to get you the maximum settlement allowed.',
  },
  {
    icon: 'ri-time-line',
    title: 'Fast Response & Dedicated Support',
    description:
      'Property damage is stressful. We respond within 24 hours, keep you informed at every stage, and handle all communication with your insurance company so you can focus on what matters.',
  },
  {
    icon: 'ri-hand-coin-line',
    title: 'Zero Upfront Cost — No Recovery, No Fee',
    description:
      "We work on a contingency basis. You pay absolutely nothing upfront and nothing at all unless we recover money for you. Our fee is a percentage of the increased settlement we win — so you always come out ahead.",
  },
];

export default function WhyChooseUs() {
  const { ref, isVisible } = useScrollAnimation();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="why-us" className="bg-[#182233] py-24 px-6 relative overflow-hidden" ref={ref}>
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#1B78D4]" />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
          <div
            className={`lg:col-span-2 lg:sticky lg:top-24 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 bg-[#1B78D4]/15 border border-[#1B78D4]/40 rounded-full px-4 py-1.5 mb-6">
              <span className="text-[#5BA8E5] text-sm font-semibold">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-6">
              Why Choose
              <span className="text-[#5BA8E5] block">Strive Claims?</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              There are thousands of insurance adjusters out there — but almost none of them work for
              <em> you</em>. We do. And we don't get paid unless you do.
            </p>
            <div className="space-y-4 mb-10">
              {[
                'Average 340% increase over initial offer',
                'Free policy review — know your rights',
                'Handle denied, underpaid & new claims',
                'Available 24/7 for emergencies',
              ].map((point) => (
                <div key={point} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-[#5BA8E5] text-lg" />
                  </div>
                  <span className="text-gray-300 text-sm">{point}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => scrollTo('booking')}
              className="bg-[#1B78D4] hover:bg-[#1560B8] text-white font-bold px-8 py-4 rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line" />
              Book Free Inspection
            </button>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-5">
            {benefits.map((benefit, i) => (
              <div
                key={benefit.title}
                className={`bg-white/5 border border-white/10 hover:border-[#1B78D4]/60 rounded-2xl p-7 transition-all duration-700 group ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#1B78D4]/10 group-hover:bg-[#1B78D4]/25 rounded-xl flex-shrink-0 border border-[#1B78D4]/30 transition-colors duration-200">
                    <i className={`${benefit.icon} text-[#5BA8E5] text-2xl`} />
                  </div>
                  <div>
                    <h3 className="text-white font-extrabold text-lg mb-2">{benefit.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
